import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { CheckIcon, ClipboardListIcon, FileTextIcon, HandshakeIcon, MessageSquareIcon, PackageIcon, PlayIcon, TagIcon, UserPlusIcon, UsersIcon } from 'lucide-react';
import type { RichTextDoc } from '@bcecloud/portal-ui';
import { addPackingLine, createSupplyItem, ruleText, saveProgramKit, type KitLine, type QuantityRule } from '@/lib/supplies';
import { addAssistant, createStaffContact, ASSISTANT_ROLES, ASSISTANT_STATUSES, STAFF_SOURCES } from '@/lib/staffing';
import { saveCharityPlan, CHARITY_OUTCOME, type CharityOutcome } from '@/lib/charities';
import { createEventContact, createEventRecording, readProgram } from '@/lib/events';
import { createContactType } from '@/lib/contact-types';
import { createTemplate, TEMPLATE_FIELD_TYPES } from '@/lib/templates';
import { postInternalMessage } from '@/lib/internal-messages';
import { CardBadge, CardBody, CardEyebrow, CardPrimaryButton, CardQuietButton, CardShell } from './card-primitives';
import { LibraryPagesCard, SaveToLibraryCard } from './library-draft-cards';
import { getCurrentChatId } from './assistant-store';
import { useAssistantSurface } from './surface';

/**
 * Draft cards: the assistant proposes a change as a `/~/draft/<kind>?…`
 * link and this module turns it into a card with the details and a Save
 * button. The write happens HERE, on Save, through the same lib function
 * the screen uses; the model only proposes. Every kind is a small Zod
 * parser over the query string, a list of facts for the card, and a save.
 */

export interface DraftFact {
  readonly label: string;
  readonly value: string;
}

export interface DraftPlan {
  /** "Draft packing line" */
  readonly eyebrow: string;
  readonly title: string;
  readonly facts: readonly DraftFact[];
  readonly saveLabel: string;
  readonly savedEyebrow: string;
  /** Where the saved thing lives, for the link after saving. */
  readonly openTo: string;
  readonly openLabel: string;
  readonly icon: React.ReactNode;
  readonly save: () => Promise<unknown>;
  /** Query keys to refresh after saving. */
  readonly invalidate: readonly (readonly unknown[])[];
}

const objectId = z.string().regex(/^[a-f0-9]{24}$/i);
const text = (max: number) => z.string().trim().min(1).max(max);
const optionalText = (max: number) => z.string().trim().max(max).optional().transform((value) => value || undefined);
const flag = z
  .string()
  .optional()
  .transform((value) => value === '1' || value === 'true');
const csv = z
  .string()
  .optional()
  .transform((value) => (value ?? '').split(',').map((part) => part.trim()).filter(Boolean));
const json = <T extends z.ZodTypeAny>(schema: T) =>
  z
    .string()
    .transform((value, ctx) => {
      try {
        return JSON.parse(value) as unknown;
      } catch {
        ctx.addIssue({ code: 'custom', message: 'not JSON' });
        return z.NEVER;
      }
    })
    .pipe(schema);

const quantityRuleSchema: z.ZodType<QuantityRule> = z.object({
  fixed: z.number().min(0).optional(),
  perTeam: z.number().min(0).optional(),
  perParticipant: z.number().min(0).optional(),
  perGroup: z.object({ quantity: z.number().min(0), participants: z.number().int().min(1) }).optional(),
  ranges: z.array(z.object({ upTo: z.number().int().min(1).optional(), quantity: z.number().min(0) })).optional(),
});
const sourcesSchema = z.array(z.object({ vendor: text(120), url: optionalText(500), sku: optionalText(80), note: optionalText(300) }));
const templateFieldSchema = z.object({
  key: z.string().regex(/^[a-z][a-zA-Z0-9-]{0,39}$/),
  label: text(80),
  type: z.enum(TEMPLATE_FIELD_TYPES as unknown as [string, ...string[]]),
  required: z.boolean().optional(),
  options: z.array(z.string()).optional(),
  hint: optionalText(200),
});

function fact(label: string, value: string | number | undefined | null): DraftFact | null {
  return value === undefined || value === null || value === '' ? null : { label, value: String(value) };
}
function facts(...rows: Array<DraftFact | null>): DraftFact[] {
  return rows.filter((row): row is DraftFact => row !== null);
}
function templateKeyFrom(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return /^[a-z]/.test(slug) ? slug : `t-${slug}`.slice(0, 40);
}
function paragraphs(body: string): RichTextDoc {
  return {
    type: 'doc',
    content: body
      .split(/\n{2,}/)
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => ({ type: 'paragraph', content: [{ type: 'text', text: part }] })),
  };
}
function eventTab(eventId: string, tab: string): string {
  return `/events/${eventId}?tab=${tab}`;
}
function outcomeLabel(outcome: string): string {
  return outcome.replace(/_/g, ' ');
}

type Planner = (params: Readonly<Record<string, string>>) => DraftPlan | null;

const packingLine: Planner = (params) => {
  const parsed = z
    .object({ event: objectId, itemId: objectId.optional(), name: optionalText(160), quantity: z.coerce.number().min(0).max(100_000).default(1), unit: optionalText(30), note: optionalText(300) })
    .refine((value) => value.itemId || value.name, 'an item or a name')
    .safeParse(params);
  if (!parsed.success) return null;
  const { event, itemId, name, quantity, unit, note } = parsed.data;
  return {
    eyebrow: 'Draft packing line',
    title: name ?? 'Catalog item',
    facts: facts(fact('Quantity', `${quantity}${unit ? ` ${unit}` : ''}`), fact('From', itemId ? 'the supplies catalog' : 'a one-off line'), fact('Note', note)),
    saveLabel: 'Add to packing list',
    savedEyebrow: 'Packing line added',
    openTo: eventTab(event, 'materials'),
    openLabel: 'Open materials',
    icon: <PackageIcon aria-hidden className="size-[18px]" />,
    save: () => addPackingLine(event, { quantity, ...(itemId ? { itemId } : {}), ...(name ? { name } : {}), ...(unit ? { unit } : {}), ...(note ? { note } : {}) }),
    invalidate: [['packing-list', event]],
  };
};

const kitLine: Planner = (params) => {
  const parsed = z.object({ program: objectId, itemId: objectId, name: optionalText(160), rule: json(quantityRuleSchema), note: optionalText(300), optional: flag }).safeParse(params);
  if (!parsed.success) return null;
  const { program, itemId, name, rule, note, optional } = parsed.data;
  const line: KitLine = { itemId, quantityRule: rule, ...(optional ? { optional: true } : {}), ...(note ? { note } : {}) };
  return {
    eyebrow: 'Draft kit line',
    title: name ?? 'Catalog item',
    facts: facts(fact('Quantity', ruleText(rule)), fact('Optional', optional ? 'Yes' : undefined), fact('Note', note)),
    saveLabel: 'Add to kit',
    savedEyebrow: 'Kit line added',
    openTo: `/programs/${program}`,
    openLabel: 'Open program',
    icon: <ClipboardListIcon aria-hidden className="size-[18px]" />,
    save: async () => {
      const existing = await readProgram(program);
      return saveProgramKit(program, [...(existing.kit ?? []), line]);
    },
    invalidate: [['program', program]],
  };
};

const supplyItem: Planner = (params) => {
  const parsed = z.object({ name: text(160), unit: optionalText(30), description: optionalText(2000), notes: optionalText(2000), sources: json(sourcesSchema).optional() }).safeParse(params);
  if (!parsed.success) return null;
  const { name, unit, description, notes, sources } = parsed.data;
  return {
    eyebrow: 'Draft supply item',
    title: name,
    facts: facts(fact('Unit', unit), fact('Description', description), fact('Where to order', sources?.map((source) => source.vendor).join(', ')), fact('Notes', notes)),
    saveLabel: 'Add to catalog',
    savedEyebrow: 'Supply item added',
    openTo: '/settings?tab=supplies',
    openLabel: 'Open supplies',
    icon: <PackageIcon aria-hidden className="size-[18px]" />,
    save: () => createSupplyItem({ name, description: description ?? '', imageUrl: '', unit: unit ?? '', notes: notes ?? '', isActive: true, sources: sources ?? [] }),
    invalidate: [['supplies']],
  };
};

const assistant: Planner = (params) => {
  const parsed = z
    .object({
      event: objectId,
      staffContactId: objectId.optional(),
      userId: objectId.optional(),
      name: optionalText(120),
      role: z.enum(ASSISTANT_ROLES as unknown as [string, ...string[]]).default('assistant'),
      callTime: optionalText(40),
      endTime: optionalText(40),
      status: z.enum(ASSISTANT_STATUSES as unknown as [string, ...string[]]).default('requested'),
      hiredBy: z.enum(STAFF_SOURCES as unknown as [string, ...string[]]).optional(),
      payRate: z.coerce.number().min(0).max(100_000).optional(),
      payBasis: z.enum(['hour', 'flat']).optional(),
      note: optionalText(1000),
    })
    .refine((value) => Boolean(value.staffContactId) !== Boolean(value.userId), 'one person')
    .safeParse(params);
  if (!parsed.success) return null;
  const { event, staffContactId, userId, name, role, callTime, endTime, status, hiredBy, payRate, payBasis, note } = parsed.data;
  return {
    eyebrow: 'Draft assistant',
    title: name ?? (userId ? 'Team member' : 'Staff contact'),
    facts: facts(
      fact('Role', role.replace(/_/g, ' ')),
      fact('Call time', callTime),
      fact('End time', endTime),
      fact('Status', status),
      fact('Hired by', hiredBy),
      fact('Pay', payRate !== undefined ? `$${payRate}${payBasis ? ` per ${payBasis === 'hour' ? 'hour' : 'event'}` : ''}` : undefined),
      fact('Note', note),
    ),
    saveLabel: 'Add assistant',
    savedEyebrow: 'Assistant added',
    openTo: eventTab(event, 'people'),
    openLabel: 'Open people',
    icon: <UsersIcon aria-hidden className="size-[18px]" />,
    save: () =>
      addAssistant(event, {
        ...(staffContactId ? { staffContactId } : {}),
        ...(userId ? { userId } : {}),
        role: role as (typeof ASSISTANT_ROLES)[number],
        status: status as (typeof ASSISTANT_STATUSES)[number],
        ...(callTime ? { callTime } : {}),
        ...(endTime ? { endTime } : {}),
        ...(hiredBy ? { hiredBy: hiredBy as (typeof STAFF_SOURCES)[number] } : {}),
        ...(payRate !== undefined ? { payRate } : {}),
        ...(payBasis ? { payBasis } : {}),
        ...(note ? { note } : {}),
      }),
    invalidate: [['event', event]],
  };
};

const staffContact: Planner = (params) => {
  const parsed = z
    .object({
      name: text(120),
      phone: optionalText(40),
      email: optionalText(120),
      city: optionalText(80),
      state: optionalText(40),
      skills: csv,
      source: z.enum(STAFF_SOURCES as unknown as [string, ...string[]]).default('wingteam'),
      notes: optionalText(2000),
    })
    .safeParse(params);
  if (!parsed.success) return null;
  const { name, phone, email, city, state, skills, source, notes } = parsed.data;
  return {
    eyebrow: 'Draft staff contact',
    title: name,
    facts: facts(fact('Phone', phone), fact('Email', email), fact('Location', [city, state].filter(Boolean).join(', ')), fact('Skills', skills.join(', ')), fact('Source', source), fact('Notes', notes)),
    saveLabel: 'Add to directory',
    savedEyebrow: 'Staff contact added',
    openTo: '/staff-contacts',
    openLabel: 'Open staff contacts',
    icon: <UserPlusIcon aria-hidden className="size-[18px]" />,
    save: () =>
      createStaffContact({
        name,
        skills,
        isActive: true,
        source: source as (typeof STAFF_SOURCES)[number],
        ...(phone ? { phone } : {}),
        ...(email ? { email } : {}),
        ...(city ? { city } : {}),
        ...(state ? { state } : {}),
        ...(notes ? { notes } : {}),
      }),
    invalidate: [['staff-contacts']],
  };
};

const charityPlan: Planner = (params) => {
  const parsed = z
    .object({
      event: objectId,
      charityId: objectId,
      charity: optionalText(160),
      outcome: z.enum(Object.values(CHARITY_OUTCOME) as [string, ...string[]]).default('pending'),
      arrivalAt: optionalText(80),
      speaker: optionalText(120),
      cell: optionalText(40),
      pickupPlan: optionalText(2000),
      talkingPoints: optionalText(4000),
      note: optionalText(2000),
    })
    .safeParse(params);
  if (!parsed.success) return null;
  const { event, charityId, charity, outcome, arrivalAt, speaker, cell, pickupPlan, talkingPoints, note } = parsed.data;
  return {
    eyebrow: 'Draft charity plan',
    title: charity ?? 'Charity',
    facts: facts(fact('Outcome', outcomeLabel(outcome)), fact('Speaker', [speaker, cell].filter(Boolean).join(', ')), fact('Arrives', arrivalAt), fact('Pickup', pickupPlan), fact('Talking points', talkingPoints), fact('Note', note)),
    saveLabel: 'Save charity plan',
    savedEyebrow: 'Charity plan saved',
    openTo: `/events/${event}`,
    openLabel: 'Open event',
    icon: <HandshakeIcon aria-hidden className="size-[18px]" />,
    save: () =>
      saveCharityPlan(event, {
        charityId,
        outcome: outcome as CharityOutcome,
        ...(speaker || cell ? { speaker: { ...(speaker ? { name: speaker } : {}), ...(cell ? { cell } : {}) } } : {}),
        ...(arrivalAt ? { arrivalAt } : {}),
        ...(pickupPlan ? { pickupPlan } : {}),
        ...(talkingPoints ? { talkingPoints } : {}),
        ...(note ? { note } : {}),
      }),
    invalidate: [['event', event]],
  };
};

const recording: Planner = (params) => {
  const parsed = z
    .object({ event: objectId, url: z.string().trim().url().max(500).refine((value) => value.startsWith('https://'), 'https'), title: text(200), recordedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(), facilitators: flag })
    .safeParse(params);
  if (!parsed.success) return null;
  const { event, url, title, recordedAt, facilitators } = parsed.data;
  return {
    eyebrow: 'Draft recording',
    title,
    facts: facts(fact('Link', url), fact('Recorded', recordedAt), fact('Who can watch', facilitators ? 'BCE team and the facilitators' : 'BCE team only')),
    saveLabel: 'Add recording',
    savedEyebrow: 'Recording added',
    openTo: `/events/${event}`,
    openLabel: 'Open event',
    icon: <PlayIcon aria-hidden className="size-[18px]" />,
    save: () => createEventRecording(event, { url, title, ...(recordedAt ? { recordedAt } : {}), viewableBy: facilitators ? ['internal', 'contractor'] : ['internal'] }),
    invalidate: [['event', event]],
  };
};

const eventContact: Planner = (params) => {
  const parsed = z
    .object({ event: objectId, typeKey: z.string().regex(/^[a-z][a-z0-9-]{0,39}$/), name: text(120), company: optionalText(120), email: optionalText(120), phone: optionalText(40), cell: optionalText(40), note: optionalText(1000) })
    .safeParse(params);
  if (!parsed.success) return null;
  const { event, typeKey, name, company, email, phone, cell, note } = parsed.data;
  return {
    eyebrow: 'Draft event contact',
    title: name,
    facts: facts(fact('Type', typeKey), fact('Company', company), fact('Email', email), fact('Phone', phone), fact('Cell', cell), fact('Note', note)),
    saveLabel: 'Add contact',
    savedEyebrow: 'Contact added',
    openTo: eventTab(event, 'people'),
    openLabel: 'Open people',
    icon: <UsersIcon aria-hidden className="size-[18px]" />,
    save: () => createEventContact(event, { typeKey, name, ...(company ? { company } : {}), ...(email ? { email } : {}), ...(phone ? { phone } : {}), ...(cell ? { cell } : {}), ...(note ? { note } : {}) }),
    invalidate: [['event', event]],
  };
};

const contactType: Planner = (params) => {
  const parsed = z.object({ label: text(60) }).safeParse(params);
  if (!parsed.success) return null;
  const { label } = parsed.data;
  return {
    eyebrow: 'Draft contact type',
    title: label,
    facts: [],
    saveLabel: 'Add contact type',
    savedEyebrow: 'Contact type added',
    openTo: '/settings?tab=contact-types',
    openLabel: 'Open contact types',
    icon: <TagIcon aria-hidden className="size-[18px]" />,
    save: () => createContactType({ label }),
    invalidate: [['contact-types']],
  };
};

const template: Planner = (params) => {
  const parsed = z
    .object({
      name: text(120),
      key: z.string().regex(/^[a-z][a-zA-Z0-9-]{0,39}$/).optional(),
      description: optionalText(500),
      defaultSubject: optionalText(200),
      category: optionalText(60),
      statuses: csv,
      fields: json(z.array(templateFieldSchema).min(1)),
    })
    .safeParse(params);
  if (!parsed.success) return null;
  const { name, key, description, defaultSubject, category, statuses, fields } = parsed.data;
  return {
    eyebrow: 'Draft note template',
    title: name,
    facts: facts(
      fact('Key', key ?? templateKeyFrom(name)),
      fact('Applies to', category ? `${category} notes` : undefined),
      fact('Statuses', statuses.join(', ')),
      fact('Fields', fields.map((field) => `${field.label} (${field.type})`).join(', ')),
      fact('Description', description),
    ),
    saveLabel: 'Add template',
    savedEyebrow: 'Template added',
    openTo: '/templates',
    openLabel: 'Open templates',
    icon: <FileTextIcon aria-hidden className="size-[18px]" />,
    save: () =>
      createTemplate({
        key: key ?? templateKeyFrom(name),
        name,
        ...(description ? { description } : {}),
        ...(defaultSubject ? { defaultSubject } : {}),
        appliesTo: category ? { noteCategory: category } : {},
        status: { enabled: statuses.length > 0, options: statuses },
        fields: fields.map((field) => ({
          key: field.key,
          label: field.label,
          type: field.type as (typeof TEMPLATE_FIELD_TYPES)[number],
          ...(field.required ? { required: true } : {}),
          ...(field.options?.length ? { options: field.options } : {}),
          ...(field.hint ? { hint: field.hint } : {}),
        })),
        logEnabled: false,
        enabled: true,
      }),
    invalidate: [['templates']],
  };
};

const message: Planner = (params) => {
  const parsed = z.object({ event: objectId, text: text(5000) }).safeParse(params);
  if (!parsed.success) return null;
  const { event, text: body } = parsed.data;
  return {
    eyebrow: 'Draft message',
    title: 'To the internal thread',
    facts: [{ label: 'Message', value: body }],
    saveLabel: 'Post message',
    savedEyebrow: 'Message posted',
    openTo: eventTab(event, 'messages'),
    openLabel: 'Open messages',
    icon: <MessageSquareIcon aria-hidden className="size-[18px]" />,
    save: () => postInternalMessage(event, { content: paragraphs(body) }),
    invalidate: [['internal-messages', event]],
  };
};

/** Every draft kind the assistant may emit, by the name in the link. */

export const DRAFT_KINDS: Readonly<Record<string, Planner>> = {
  'packing-line': packingLine,
  'kit-line': kitLine,
  'supply-item': supplyItem,
  assistant,
  'staff-contact': staffContact,
  'charity-plan': charityPlan,
  recording,
  'event-contact': eventContact,
  'contact-type': contactType,
  template,
  message,
};

/**
 * Turns a draft link into a plan, or null when the kind is unknown or the
 * fields do not parse (the card then says so instead of guessing).
 *
 * @param kind - The `<kind>` from `/~/draft/<kind>`.
 * @param params - The decoded query fields.
 * @returns The plan, or null.
 */
export function planDraft(kind: string, params: Readonly<Record<string, string>>): DraftPlan | null {
  const planner = DRAFT_KINDS[kind.toLowerCase()];
  return planner ? planner(params) : null;
}

type DraftCardProps = { readonly kind: string; readonly params: Readonly<Record<string, string>> };

/**
 * Draft kinds that need more than facts and Save, so they bring their own
 * card: the page library's Add (thumbnail, Preview) and Save (editable).
 * Their link parsers (`planLibraryPagesDraft`, `planSaveToLibraryDraft`)
 * live with the cards.
 */
const CUSTOM_DRAFT_CARDS: Readonly<Record<string, (props: { readonly params: Readonly<Record<string, string>> }) => React.ReactElement>> = {
  'library-pages': LibraryPagesCard,
  'save-to-library': SaveToLibraryCard,
};

/** A draft link's card: a custom one for the kinds above, the planned card otherwise. */
export function DraftActionCard({ kind, params }: DraftCardProps): React.ReactElement {
  const Custom = CUSTOM_DRAFT_CARDS[kind.toLowerCase()];
  return Custom ? <Custom params={params} /> : <PlannedDraftCard kind={kind} params={params} />;
}

/** Where to finish a draft outside the editor: this chat when it is saved, else the record's page. */
function finishInAppPath(fallback: string): string {
  const chatId = getCurrentChatId();
  return chatId ? `/assistant/chats/${chatId}` : fallback;
}

/**
 * One card for every planned draft kind: the facts, Save and Dismiss, a saved
 * state with a link to where the record now lives, and an error line.
 */
function PlannedDraftCard({ kind, params }: DraftCardProps): React.ReactElement {
  const [dismissed, setDismissed] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { savesRecords } = useAssistantSurface();
  const plan = planDraft(kind, params);
  const save = useMutation({
    mutationFn: async () => {
      if (!plan) throw new Error('Nothing to save.');
      await plan.save();
    },
    onSuccess: async () => {
      await Promise.all((plan?.invalidate ?? []).map((queryKey) => queryClient.invalidateQueries({ queryKey })));
    },
  });

  if (!plan) {
    return <span className="my-1 block text-admin-micro text-admin-muted italic">The assistant's draft could not be read. Ask it to try again.</span>;
  }
  if (dismissed) {
    return <span className="my-1 block text-admin-micro text-admin-muted italic">{plan.eyebrow} dismissed.</span>;
  }

  return (
    <CardShell>
      <CardBody>
        <span className="flex items-start gap-3">
          <CardBadge>{plan.icon}</CardBadge>
          <span className="min-w-0 flex-1">
            <CardEyebrow>{save.isSuccess ? plan.savedEyebrow : plan.eyebrow}</CardEyebrow>
            <span className="mt-0.5 block font-bold text-portal-ink">{plan.title}</span>
          </span>
          {save.isSuccess ? <CheckIcon aria-hidden className="size-4 shrink-0 text-emerald-600" /> : null}
        </span>

        {plan.facts.length > 0 ? (
          <span className="mt-2 block rounded-lg bg-admin-bg px-2.5 py-2">
            {plan.facts.map((row) => (
              <span key={row.label} className="block text-admin-micro">
                <span className="font-semibold text-admin-muted">{row.label}: </span>
                <span className="whitespace-pre-wrap text-portal-ink">{row.value}</span>
              </span>
            ))}
          </span>
        ) : null}

        {save.isError ? <span className="mt-2 block text-admin-micro font-semibold text-red-600">Could not save. Try again.</span> : null}

        {save.isSuccess ? (
          <span className="mt-2.5 block">
            <Link to={plan.openTo} className="text-admin-micro font-bold text-brand no-underline hover:underline">
              {plan.openLabel}
            </Link>
          </span>
        ) : !savesRecords ? (
          // Inside Salesforce the editor's token cannot write this record, so
          // hand off to the same chat in BCE Cloud, where Save works.
          <span className="mt-2.5 block">
            <span className="block text-admin-micro text-admin-muted">Save this from BCE Cloud. The chat opens there in a new tab.</span>
            <span className="mt-1.5 flex items-center gap-2">
              <CardPrimaryButton onClick={() => navigate(finishInAppPath(plan.openTo))}>Finish in BCE Cloud</CardPrimaryButton>
              <CardQuietButton onClick={() => setDismissed(true)}>Dismiss</CardQuietButton>
            </span>
          </span>
        ) : (
          <span className="mt-2.5 flex items-center gap-2">
            <CardPrimaryButton onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : plan.saveLabel}
            </CardPrimaryButton>
            <CardQuietButton onClick={() => setDismissed(true)} disabled={save.isPending}>
              Dismiss
            </CardQuietButton>
          </span>
        )}
      </CardBody>
    </CardShell>
  );
}
